import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import arrayUtilities from "../../../utilities/arrayUtilities";
import { Polygon, Marker } from "react-leaflet";
import EditableRectangle from "../editableRectangle";
import postal from "postal";
import ConfigurationDialog from "./_configurationDialog";
import WeightSelect from "../../form/weightSelect";
import ColorPicker from "../../form/colorPicker";
import OpacitySlider from "../../form/opacitySlider";
import WorkflowSelect from "../../form/workflowSelect";
import stringUtilities from "../../../utilities/stringUtilities";
import geospatialUtilities from "../../../utilities/geospatialUtilities";
import * as turf from '@turf/turf'
import MapPopup from "./_mapPopup";
import LimitSelect from "../../form/limitSelect";
import hash from "object-hash";
import sketchUtilities from "../../../utilities/sketchUtilities";
import { DrawingNoteWithPresets } from "../../form/drawingNoteWithPresets";
import FontSizeSelect from "../../form/fontSizeSelect";
import WidthSlider from "../../form/widthSlider";
import RotationSlider from "../../form/rotationSlider";
import { getTextMarkerIcon } from "./getTextMarkerIcon";
import mapIcons from "../mapIcons";

export default function BoxDrawingTool(props) {

    const [isDrawing, setIsDrawing] = useState(props.isDrawing);
    const [isEditing, setIsEditing] = useState(props.isEditing);
    const [isEditingNote, setIsEditingNote] = useState(false);    
    const [isVisible, setIsVisible] = useState(false);

    const [printingSketchLimitIndex, setPrintingSketchLimitIndex] = useState(null);
    const [printingWorkflowId, setPrintingWorkflowId] = useState(null);
    const [renderPositions, setRenderPositions] = useState(null);

    const [northEast, setNorthEast] = useState(null);
    const [southWest, setSouthWest] = useState(null);
    const [noteRotate, setNoteRotate] = useState(null);
    const [noteWidth, setNoteWidth] = useState(null);
    const [boxRotate, setBoxRotate] = useState(null);
    const [noteFontsize, setNoteFontsize] = useState(null);
    const [noteColor, setNoteColor] = useState(null);    
    const [text, setText] = useState(null);
    const [weight, setWeight] = useState(null);
    const [color, setColor] = useState(null);
    const [opacity, setOpacity] = useState(null);
    const [fillOpacity, setFillOpacity] = useState(null);
    const [workflowId, setWorkflowId] = useState(null);
    const [limitId, setLimitId] = useState(null);
    const [dragOffset, setDragOffset] = useState(null);

    const reset = function () {

        let coords = props.sketchItem?.coords ?? [];

        setNorthEast(!arrayUtilities.isNullOrEmpty(coords) && coords.length > 0 ? coords[0] : null);
        setSouthWest(!arrayUtilities.isNullOrEmpty(coords) && coords.length > 0 ? coords[1] : null);
        setWeight(props.sketchItem?.tool?.properties?.weight ?? 1);
        setNoteColor(props.sketchItem?.tool?.properties?.noteColor ?? "#000000")
        setNoteFontsize(props.sketchItem?.tool?.properties?.noteFontsize ?? "font-smaller")
        setNoteWidth(props.sketchItem?.tool?.properties?.noteWidth ?? 0)
        setNoteRotate(props.sketchItem?.tool?.properties?.noteRotate ?? 0)
        setBoxRotate(props.sketchItem?.tool?.properties?.boxRotate ?? 0)
        setText(props.sketchItem?.tool?.properties?.text ?? "")
        setColor(props.sketchItem?.tool?.properties?.color ?? "#000000");
        setOpacity(props.sketchItem?.tool?.properties?.opacity ?? 1);
        setFillOpacity(props.sketchItem?.tool?.properties?.fillOpacity ?? 0.5);
        setWorkflowId(props.sketchItem?.tool?.properties?.workflowId ?? "");
        setLimitId(props.sketchItem?.tool?.properties?.limitId ?? props.activeLimit);
    }

    const onRectangleChange = function (northEast, southWest) {
        setNorthEast(northEast);
        setSouthWest(southWest);
        setIsDrawing(false);
        setDragOffset(null);
    }

    const onRectangleDragMove = useCallback((latDiff, lngDiff) => {
        setDragOffset({ lat: latDiff, lng: lngDiff });
    }, []);

    const onDelete = function () {

        if (props.onDelete) {
            props.onDelete(props.index);
        }
    }

    const onCancel = function () {

        if (props.onCancel) {
            props.onCancel();
        }

        setIsDrawing(false);
        setIsEditing(false);
        reset();
    }

    const onSave = function () {

        if (props.onSave) {

            let sketchItem = {
                coords: [
                    northEast,
                    southWest
                ],
                tool: {
                    id: 23,
                    properties: {
                        weight,
                        color,
                        opacity,
                        fillOpacity,
                        workflowId,
                        limitId,
                        text,
                        noteColor,
                        noteFontsize,
                        noteRotate,
                        noteWidth,
                        boxRotate
                    }
                }
            }
            props.onSave(sketchItem, props.index);
        }
        
        setIsDrawing(props.isDrawing)
        setIsEditing(props.isEditing);
        reset();
    }

    useEffect(() => {

        if (isEditing) {
            postal.publish({
                channel: "map",
                topic: "isDrawingOrEditing",
                data: { value: true }
            })
        }

    }, [isEditing])

    useEffect(() => {
        if (northEast !== null && southWest !== null) {
            const positions = geospatialUtilities.boundsToLatLngArray([northEast, southWest]);
            const midLat = (northEast.lat + southWest.lat) / 2;
            const midLng = (northEast.lng + southWest.lng) / 2;
            if (boxRotate && boxRotate !== 0) {
                const geoCoords = positions.map(([lat, lng]) => [lng, lat]);
                const rotated = turf.transformRotate(
                    turf.polygon([geoCoords]), 
                    boxRotate, 
                    { pivot: [midLng, midLat] }
                );
                setRenderPositions(rotated.geometry.coordinates[0].map(([lng, lat]) => [lat, lng]));
            } else {
                setRenderPositions(positions);
            }
        } else {
            setRenderPositions(null);
        }
    }, [northEast, southWest, boxRotate])

    useEffect(() => {

        let pointsLinesIsDefault = arrayUtilities.isNullOrEmpty(props.workflowPoints) && arrayUtilities.isNullOrEmpty(props.workflowLines) && arrayUtilities.isNullOrEmpty(props.workflowPointsLines);
        let workflowIsVisible = stringUtilities.isNullOrEmpty(workflowId) || pointsLinesIsDefault || props.workflowPoints.includes(workflowId) || props.workflowLines.includes(workflowId) || props.workflowPointsLines.includes(workflowId);

        setIsVisible(isDrawing || isEditing || workflowIsVisible);
    }, [isDrawing, isEditing, workflowId, props.workflowLines, props.workflowPoints, props.workflowPointsLines])

    useEffect(() => {
        if (northEast === null || southWest === null) return;

        // Rotation must be applied before intersection — the limit clip must use the rotated shape,
        // not the axis-aligned bounding box.
        const base = geospatialUtilities.boundsToLatLngArray([northEast, southWest]);
        let positions;
        if (boxRotate && boxRotate !== 0) {
            const midLat = (northEast.lat + southWest.lat) / 2;
            const midLng = (northEast.lng + southWest.lng) / 2;
            const geoCoords = base.map(([lat, lng]) => [lng, lat]);
            const rotated = turf.transformRotate(
                turf.polygon([geoCoords]), 
                boxRotate, 
                { pivot: [midLng, midLat] }
            );
            positions = rotated.geometry.coordinates[0].map(([lng, lat]) => [lat, lng]);
        } else {
            positions = base;
        }

        if (printingSketchLimitIndex !== null || !stringUtilities.isNullOrEmpty(printingWorkflowId)) {

            if (!sketchUtilities.isSketchObjectLimitCurrentlyPrinting(limitId, props.sketch?.limits, printingSketchLimitIndex)) {
                setRenderPositions(null);
                return;
            }

            if (!stringUtilities.isNullOrEmpty(workflowId)) {
                let workflow = arrayUtilities.isNullOrEmpty(props.workflows) ? null : props.workflows.find(x => x.id === workflowId);
                if (!stringUtilities.isNullOrEmpty(printingWorkflowId) && printingWorkflowId !== "ALL" && printingWorkflowId !== workflowId && (!workflow || workflow === null || !workflow.isComposite)) {
                    setRenderPositions(null);
                    return;
                }
            }

            if (printingSketchLimitIndex !== null && !isNaN(printingSketchLimitIndex) && props.sketch && props.sketch !== null && !arrayUtilities.isNullOrEmpty(props.sketch.limits) && props.sketch.limits.length > printingSketchLimitIndex) {
                const limit = props.sketch.limits[printingSketchLimitIndex];
                const p1 = turf.polygon([positions]);
                const p2 = turf.polygon([geospatialUtilities.boundsToLatLngArray(limit.limitOfSketchCoords)]);
                const intersection = turf.intersect(p1, p2);
                setRenderPositions(intersection ? intersection.geometry.coordinates[0] : null);
                return;
            }
        }

        setRenderPositions(positions);
    }, [printingSketchLimitIndex, printingWorkflowId, boxRotate])

    useEffect(() => {
        reset();

        const subscriptions = [
            postal.subscribe({
                channel: "map",
                topic: "printing",
                callback: function (data) {
                    setPrintingSketchLimitIndex(data.sketchLimitIndex);
                    setPrintingWorkflowId(data.workflowId);
                }
            }),
        ]

        return () => {
            subscriptions.forEach(subscription => {
                subscription.unsubscribe();
            });
        }
    }, [])

    const pathOptions = useMemo(
        () => ({
            color,
            weight,
            fill: true,
            opacity,
            fillColor: color,
            fillOpacity
        }),
        [color, weight, opacity, fillOpacity]
    );

    const editingPathOptions = useMemo(
        () => ({
            ...pathOptions,
            opacity: 0,
            fillOpacity: 0,
            weight: 0
        }),
        [pathOptions]
    );

    // During printing, renderPositions holds visibility-filtered and limit-clipped coordinates
    // computed by the printing useEffect — use it directly so print output is correct.
    // Outside printing, compute from raw state synchronously to avoid the async useEffect gap
    // that would cause a one-frame snap after a whole-box drag commits.
    const isPrinting = printingSketchLimitIndex !== null || !stringUtilities.isNullOrEmpty(printingWorkflowId);

    const displayPositions = useMemo(() => {
        if (isPrinting) {
            if (!renderPositions) {
                return null
            };
            if (!dragOffset) {
                return renderPositions
            };
            return renderPositions.map(([lat, lng]) => [lat + dragOffset.lat, lng + dragOffset.lng]);
        }
        if (!northEast || !southWest) {
            return null;
        }
        const base = geospatialUtilities.boundsToLatLngArray([northEast, southWest]);
        const midLat = (northEast.lat + southWest.lat) / 2;
        const midLng = (northEast.lng + southWest.lng) / 2;
        let positions = base;
        if (boxRotate && boxRotate !== 0) {
            const geoCoords = base.map(([lat, lng]) => [lng, lat]);
            const rotated = turf.transformRotate(
                turf.polygon([geoCoords]), 
                boxRotate, 
                { pivot: [midLng, midLat] }
            );
            positions = rotated.geometry.coordinates[0].map(([lng, lat]) => [lat, lng]);
        }
        if (dragOffset) {
            return positions.map(([lat, lng]) => [lat + dragOffset.lat, lng + dragOffset.lng]);
        }
        return positions;
    }, [northEast, southWest, boxRotate, dragOffset, renderPositions, isPrinting]);

    const displayMidPoint = useMemo(() => {
        if (!northEast || !southWest) return null;
        return [
            (northEast.lat + southWest.lat) / 2 + (dragOffset?.lat ?? 0),
            (northEast.lng + southWest.lng) / 2 + (dragOffset?.lng ?? 0)
        ];
    }, [northEast, southWest, dragOffset]);

    const handleDeleteNote = () => {
        if (props.onSave) {

            let sketchItem = {
                coords: [
                    northEast,
                    southWest
                ],
                tool: {
                    id: 23,
                    properties: {
                        weight,
                        color,
                        opacity,
                        fillOpacity,
                        workflowId,
                        limitId,
                        boxRotate
                    }
                }
            }

            props.onSave(sketchItem, props.index);
        }
    }

    const rotatedDragStartRef = useRef(null);
    const polygonDragStartRef = useRef(null);

    const onPolygonMouseDown = useCallback((e) => {
        polygonDragStartRef.current?.(e.latlng);
    }, []);
    // Always-current values in a ref so all drag handlers can be stable (useCallback with [] deps)
    // and still read the latest state without stale closures.
    const latestRef = useRef({ northEast, southWest, boxRotate });
    latestRef.current = { northEast, southWest, boxRotate };

    const onRotatedCornerDragStart = useCallback(() => {
        const { northEast, southWest } = latestRef.current;
        rotatedDragStartRef.current = { northEast: { ...northEast }, southWest: { ...southWest } };
    }, []);

    const onRotatedCornerDrag = useCallback((e, isNorthEast, isSouthEast, isSouthWest, isNorthWest) => {
        const { boxRotate } = latestRef.current;
        const start = rotatedDragStartRef.current ?? latestRef.current;
        const pivotLat = (start.northEast.lat + start.southWest.lat) / 2;
        const pivotLng = (start.northEast.lng + start.southWest.lng) / 2;
        const pt = turf.point([e.latlng.lng, e.latlng.lat]);
        const unrotated = turf.transformRotate(pt, -boxRotate, { pivot: [pivotLng, pivotLat] });
        const [lng, lat] = unrotated.geometry.coordinates;
        const p = { lat, lng };

        let ne = { ...start.northEast };
        let sw = { ...start.southWest };

        if (isNorthEast) { 
            ne = p; 
        }
        else if (isSouthEast) { 
            ne = { 
                lat: ne.lat, 
                lng: p.lng 
            }; 
            sw = { 
                lat: p.lat, 
                lng: sw.lng 
            }; 
        }
        else if (isSouthWest) { 
            sw = p; 
        }
        else if (isNorthWest) { 
            ne = { 
                lat: p.lat, 
                lng: ne.lng 
            }; 
            sw = { 
                lat: sw.lat, 
                lng: p.lng 
            }; 
        }

        setNorthEast(
            { 
                lat: Math.max(ne.lat, sw.lat), 
                lng: Math.max(ne.lng, sw.lng) 
            }
        );
        setSouthWest(
            { 
                lat: Math.min(ne.lat, sw.lat), 
                lng: Math.min(ne.lng, sw.lng) 
            }
        );
    }, []);

    const onRotatedCornerDragEnd = useCallback(() => {
        rotatedDragStartRef.current = null;
    }, []);

    const onNeDrag = useCallback((e) => 
        onRotatedCornerDrag(e, true, false, false, false), 
    [onRotatedCornerDrag]);

    const onNwDrag = useCallback((e) => 
        onRotatedCornerDrag(e, false, false, false, true), 
    [onRotatedCornerDrag]);

    const onSwDrag = useCallback((e) => 
        onRotatedCornerDrag(e, false, false, true, false), 
    [onRotatedCornerDrag]);

    const onSeDrag = useCallback((e) => 
        onRotatedCornerDrag(e, false, true, false, false), 
    [onRotatedCornerDrag]);

    const neEventHandlers = useMemo(() => {
        return {
            mousedown: onRotatedCornerDragStart,
            drag: onNeDrag,
            dragend: onRotatedCornerDragEnd
        };
    }, [onRotatedCornerDragStart, onNeDrag, onRotatedCornerDragEnd]);

    const nwEventHandlers = useMemo(() => {
        return {
            mousedown: onRotatedCornerDragStart,
            drag: onNwDrag,
            dragend: onRotatedCornerDragEnd
        };
    }, [onRotatedCornerDragStart, onNwDrag, onRotatedCornerDragEnd]);

    const swEventHandlers = useMemo(() => {
        return {
            mousedown: onRotatedCornerDragStart,
            drag: onSwDrag,
            dragend: onRotatedCornerDragEnd
        };
    }, [onRotatedCornerDragStart, onSwDrag, onRotatedCornerDragEnd]);

    const seEventHandlers = useMemo(() => {
        return {
            mousedown: onRotatedCornerDragStart,
            drag: onSeDrag,
            dragend: onRotatedCornerDragEnd
        };
    }, [onRotatedCornerDragStart, onSeDrag, onRotatedCornerDragEnd]);

    const icon = useMemo(
        () => getTextMarkerIcon(`box-text-marker-${props.index}`, text, noteColor, noteFontsize, noteRotate, noteWidth),
        [text, noteColor, noteFontsize, noteRotate, noteWidth]
    )

    return <>

        {(isDrawing || isEditing) &&

            <EditableRectangle isDrawing={ isDrawing } pathOptions={ isEditing && boxRotate ? editingPathOptions : pathOptions } key={ hash({ northEast, southWest }) } northEast={ northEast } southWest={ southWest } onChange={ onRectangleChange } onDragMove={ isEditing && boxRotate ? onRectangleDragMove : undefined } allowResize={ !(isEditing && boxRotate) } dragStartRef={ polygonDragStartRef } noteComponent={
                northEast && southWest && !stringUtilities.isNullOrEmpty(text) &&
                <Marker icon={ icon } position={ displayMidPoint } pane="drawing-tool-labels" />
            } />
        }
        {isEditing && boxRotate && !arrayUtilities.isNullOrEmpty(displayPositions) && <>
            <Marker draggable position={ displayPositions[0] } icon={ mapIcons.dragHandleNesw } eventHandlers={ neEventHandlers } />
            <Marker draggable position={ displayPositions[1] } icon={ mapIcons.dragHandleNwse } eventHandlers={ nwEventHandlers } />
            <Marker draggable position={ displayPositions[2] } icon={ mapIcons.dragHandleNesw } eventHandlers={ swEventHandlers } />
            <Marker draggable position={ displayPositions[3] } icon={ mapIcons.dragHandleNwse } eventHandlers={ seEventHandlers } />
        </>}
        {!isDrawing && (!isEditing || (boxRotate && boxRotate !== 0)) && isVisible && !arrayUtilities.isNullOrEmpty(displayPositions) &&
            <Polygon positions={displayPositions} pathOptions={pathOptions} eventHandlers={ isEditing && boxRotate ? { mousedown: onPolygonMouseDown } : {} }>
                {!isEditing && <MapPopup title="Box" onEdit={() => setIsEditing(true)} deleteTitle="Delete Box" deleteContent="Are you sure you want to permanently delete this box?" onDelete={onDelete} />}
                {!stringUtilities.isNullOrEmpty(text) && <Marker icon={ icon } position={ displayMidPoint } pane="drawing-tool-labels">
                    {!isEditing && !isEditingNote && <MapPopup
                        title="Note"
                        onEdit={ () => setIsEditingNote(true) }
                        deleteTitle="Delete Note"
                        deleteContent="Are you sure you want to permanently delete this note"
                        onDelete={ () => handleDeleteNote() } />}
                </Marker>}
            </Polygon>
        }

        {
            isEditingNote && 
            <>
                <ConfigurationDialog show={ isEditingNote } title="Edit Note" onCancel={ () => {
                    setIsEditingNote(false)
                    reset()
                } } onSave={ () => {
                    if (props.onSave) {
                        let sketchItem = {
                            coords: [
                                northEast,
                                southWest
                            ],
                            tool: {
                                id: 23,
                                properties: {
                                    weight,
                                    color,
                                    opacity,
                                    fillOpacity,
                                    workflowId,
                                    limitId,
                                    text,
                                    noteColor,
                                    noteFontsize,
                                    noteRotate,
                                    noteWidth,
                                    boxRotate
                                }
                            }
                        }

                        props.onSave(sketchItem, props.index);
                    }
                    setIsEditingNote(false)
                } }>
                    <DrawingNoteWithPresets text={text} setText={setText} />
                    <FontSizeSelect value={ noteFontsize } onChange={ setNoteFontsize } />
                    <ColorPicker value={ noteColor } onChange={ setNoteColor } />
                    <RotationSlider value={ noteRotate } onChange={ setNoteRotate } />
                    <WidthSlider value={ noteWidth } onChange={ setNoteWidth } />
                </ConfigurationDialog>
            </>
        }

        <ConfigurationDialog show={isDrawing || isEditing} title={`${isDrawing ? "Add" : "Edit"} Box`} onCancel={onCancel} onSave={onSave}>
            <DrawingNoteWithPresets text={text} setText={setText} />
            <WeightSelect value={weight} onChange={setWeight} />
            <ColorPicker value={color} onChange={setColor} />
            <RotationSlider value={boxRotate} onChange={setBoxRotate} disabled={!northEast || !southWest} />
            <OpacitySlider value={opacity} onChange={setOpacity} />
            <OpacitySlider label="Fill Opacity" value={fillOpacity} onChange={setFillOpacity} />
            <WorkflowSelect value={workflowId} onChange={setWorkflowId} workflows={props.workflows} />
            <LimitSelect value={limitId} onChange={setLimitId} limits={props.sketch?.limits} />
        </ConfigurationDialog>
    </>
}